import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { Icon, Button, Modal, Form, Input, Dropdown, Progress, Segment, Confirm } from "semantic-ui-react";
import { axiosBasic, getUserInfo } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";

import "./Danger.css";
import "../Settings.css";

const AccountAPI = config.REST_API.Account;

const DangerZone = () => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeactivateSuccessModal, setShowDeactivateSuccessModal] = useState(false);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get company ID from localStorage
  const getCompanyId = () => {
    try {
      const selectedCompany = localStorage.getItem('selectedCompany');
      if (selectedCompany) {
        const company = JSON.parse(selectedCompany);
        return company.id;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const dangerActions = [
    {
      id: "deactivate",
      name: t("app.dangerZone.deactivateAccount"),
      description: t("app.dangerZone.deactivateAccountDescription"),
      icon: "pause circle",
      color: "#fa709a",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      severity: "danger",
      action: () => setShowDeactivateModal(true)
    },
    {
      id: "delete",
      name: t("app.dangerZone.deleteCompanyAccount"),
      description: t("app.dangerZone.deleteCompanyAccountDescription"),
      icon: "trash alternate",
      color: "#ff6b6b",
      gradient: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
      severity: "critical",
      action: () => setShowDeleteModal(true)
    }
  ];

  const deletionConsequences = [
    {
      icon: "users",
      title: "All Users Removed",
      description: "Every team member will lose access immediately."
    },
    {
      icon: "calendar",
      title: "Calendar Data Lost",
      description: "All leave requests, holidays, and schedules will be deleted."
    },
    {
      icon: "database",
      title: "Company Data Erased",
      description: "Employee records, settings, and configurations will be removed."
    },
    {
      icon: "stop circle",
      title: "Billing Stopped",
      description: "No more charges, but no refunds for unused time."
    },
    {
      icon: "plug",
      title: "Integrations Disabled",
      description: "API connections and webhooks will stop working."
    },
    {
      icon: "undo",
      title: "No Recovery Possible",
      description: "This action is permanent and cannot be undone."
    },
    {
      icon: "calculator",
      title: t("app.dangerZone.consequencePayrollTitle"),
      description: t("app.dangerZone.consequencePayrollDescription")
    },
    {
      icon: "clock outline",
      title: t("app.dangerZone.consequenceAttendanceTitle"),
      description: t("app.dangerZone.consequenceAttendanceDescription")
    },
    {
      icon: "folder open",
      title: t("app.dangerZone.consequenceDocumentsTitle"),
      description: t("app.dangerZone.consequenceDocumentsDescription")
    },
    {
      icon: "table",
      title: t("app.dangerZone.consequenceTimesheetsTitle"),
      description: t("app.dangerZone.consequenceTimesheetsDescription")
    }
  ];

  const handleExport = () => {
    setIsExporting(true);
    setExportProgress(0);

    const interval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsExporting(false);
          setShowExportModal(false);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleDeactivateAccount = async () => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    setIsDeactivating(true);
    try {
      const response = await axiosBasic.get(AccountAPI.DeactivateAccount);

      const success = response.data && response.data.success !== false;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("deactivateAccount", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to deactivate account"),
        });
      }

      if (success) {
        setShowDeactivateModal(false);
        setShowDeactivateSuccessModal(true);
        // Clear localStorage and navigate to login after showing success message
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        alert(response.data?.message || "Failed to deactivate account. Please try again.");
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("deactivateAccount", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      alert(error.response?.data?.message || "An error occurred while deactivating your account. Please try again.");
    } finally {
      setIsDeactivating(false);
      setShowDeactivateModal(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== "DELETE") {
      return;
    }

    const companyId = getCompanyId();
    if (!companyId) {
      alert("Company ID not found. Please refresh the page and try again.");
      return;
    }

    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { companyId };
    setIsDeleting(true);
    try {
      const payload = {
        CompanyId: companyId,
        ConfirmDelete: true
      };

      const response = await axiosBasic.delete(AccountAPI.DeleteCompanyAccount, {
        data: payload
      });

      const success = response.data && response.data.success !== false;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("deleteCompanyAccount", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to delete company account"),
        });
      }

      if (success) {
        setShowDeleteModal(false);
        setShowDeleteSuccessModal(true);
        // Clear localStorage and redirect to login after showing success message
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        alert(response.data?.message || "Failed to delete company account. Please try again.");
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("deleteCompanyAccount", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      alert(error.response?.data?.message || "An error occurred while deleting your company account. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmation("");
    }
  };

  return (
    <div className="danger-zone-container">
      {/* Header Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="warning circle" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("app.dangerZone.title")}</h3>
            <p className="section-description">
              {t("app.dangerZone.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="settings-page-content">
        <div className="content-card">
          {/* Warning Alert */}
          <div className="danger-warning-alert">
            <div className="warning-icon">
              <Icon name="warning sign" size="large" />
            </div>
            <div className="warning-content">
              <h3>⚠️ Tread carefully, here be dragons!</h3>
              <p>
                The actions in this section are <strong>irreversible</strong> and will permanently
                affect your account. For Backup, you can contact support team to get your data exported.
              </p>
            </div>
          </div>

          {/* Info Section - hidden for now, will be implemented later */}
          {/* <div className="info-section">
            <div className="info-icon">
              <Icon name="info circle" />
            </div>
            <div className="info-text">
              <h3>Account Management</h3>
              <p>
                Before making any permanent changes, we strongly recommend exporting your data.
                Once deleted, there is no way to recover your information.
              </p>
            </div>
          </div> */}

          {/* Danger Actions */}
          <div className="danger-actions-section">
            <h3 className="section-title">
              <Icon name="exclamation triangle" />
              Critical Actions
            </h3>
            <p className="section-description">These actions will permanently affect your account</p>

            <div className="danger-actions-grid">
              {dangerActions.map(action => (
                <div key={action.id} className={`danger-action-card ${action.severity}`}>
                  <div className="action-icon" style={{ background: action.gradient }}>
                    <Icon name={action.icon} />
                  </div>
                  <div className="action-info">
                    <h4 className="action-name">{action.name}</h4>
                    <p className="action-description">{action.description}</p>
                    <div className="action-severity">
                      <span className={`severity-badge ${action.severity}`}>
                        {action.severity === 'warning' && '⚠️ Warning'}
                        {action.severity === 'danger' && '🚨 Danger'}
                        {action.severity === 'critical' && '💀 Critical'}
                      </span>
                    </div>
                  </div>
                  <div className="action-button">
                    <button
                      className={`action-btn ${action.severity}`}
                      onClick={action.action}
                      disabled={action.id === "deactivate" && isDeactivating}
                      style={{
                        background: action.gradient,
                        cursor: (action.id === "deactivate" && isDeactivating) ? "not-allowed" : "pointer",
                        opacity: (action.id === "deactivate" && isDeactivating) ? 0.6 : 1
                      }}
                    >
                      <Icon name={action.id === "deactivate" && isDeactivating ? "spinner" : action.icon} loading={action.id === "deactivate" && isDeactivating} />
                      {action.id === "deactivate" && isDeactivating ? "Deactivating..." : action.name}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Deletion Consequences */}
          <div className="deletion-consequences-section">
            <h3 className="section-title">
              <Icon name="trash alternate" />
              What Happens When You Delete
            </h3>
            <p className="section-description">Understanding the permanent consequences of account deletion</p>

            <div className="consequences-grid">
              {deletionConsequences.map((consequence, index) => (
                <div key={index} className="consequence-card">
                  <div className="consequence-icon">
                    <Icon name={consequence.icon} />
                  </div>
                  <div className="consequence-content">
                    <h4>{consequence.title}</h4>
                    <p>{consequence.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Backup Recommendation - hidden for now, will be implemented later */}
          {/* <div className="backup-recommendation-section">
            <div className="backup-card">
              <div className="backup-icon">
                <Icon name="cloud download" />
              </div>
              <div className="backup-content">
                <h3>📦 Create a Backup First</h3>
                <p>
                  Before making any permanent changes, download a complete backup of your data.
                  This includes all employee records, leave history, settings, and configurations.
                </p>
                <div className="backup-features">
                  <span className="backup-feature">
                    <Icon name="check circle" />
                    Complete data export
                  </span>
                  <span className="backup-feature">
                    <Icon name="check circle" />
                    JSON format
                  </span>
                  <span className="backup-feature">
                    <Icon name="check circle" />
                    Includes all settings
                  </span>
                </div>
                <button
                  className="backup-btn"
                  onClick={() => setShowExportModal(true)}
                >
                  <Icon name="download" />
                  Download Backup
                </button>
              </div>
            </div>
          </div> */}

          {/* Final Warning - hidden for now, will be implemented later */}
          {/* <div className="final-warning-section">
            <div className="final-warning-card">
              <div className="warning-header">
                <Icon name="exclamation triangle" size="large" />
                <h3>Final Warning</h3>
              </div>
              <p>
                <strong>There is no going back.</strong> Once you delete your account, all data will be
                permanently erased and cannot be recovered. Make sure you have exported everything you need.
              </p>
              <div className="warning-actions">
                <button className="warning-btn secondary">
                  <Icon name="question circle" />
                  Contact Support
                </button>
                <button className="warning-btn primary">
                  <Icon name="download" />
                  Export Data First
                </button>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Export Data Modal */}
      <Modal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        size="small"
        className="modern-modal"
      >
        <Modal.Header style={{
          background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
          color: "white",
          borderRadius: "12px 12px 0 0"
        }}>
          <Icon name="download" />
          Export Company Data
        </Modal.Header>
        <Modal.Content>
          <div className="export-content">
            <div className="export-info">
              <h4>What's included in the export:</h4>
              <ul>
                <li>All employee records and profiles</li>
                <li>Complete leave history and requests</li>
                <li>Company settings and configurations</li>
                <li>Department and team structures</li>
                <li>Public holidays and calendar data</li>
                <li>API keys and integration settings</li>
              </ul>
            </div>

            {isExporting ? (
              <div className="export-progress">
                <Progress
                  percent={exportProgress}
                  color="blue"
                  size="small"
                  style={{ margin: '16px 0' }}
                />
                <p>Preparing your data export... {exportProgress}%</p>
              </div>
            ) : (
              <div className="export-actions">
                <p>This will create a complete backup of all your data in JSON format.</p>
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: "0 0 12px 12px",
          padding: "16px 24px"
        }}>
          <Button onClick={() => setShowExportModal(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleExport}
            disabled={isExporting}
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              border: "none"
            }}
          >
            {isExporting ? 'Exporting...' : 'Start Export'}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Deactivate Account Confirmation Modal */}
      <Modal
        open={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        size="tiny"
        dimmer="blurring"
        centered
        className="deactivate-account-modal"
        style={{
          height: "fit-content",
          margin: "auto",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
        }}
      >
        <Modal.Header
          className="deactivate-modal-header"
          style={{
            borderBottom: "1px solid #e9ecef",
            padding: "24px 24px 16px 24px",
            background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
            backgroundColor: "transparent",
            color: "white",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="pause circle" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 className="mb-1 fw-bold" style={{ color: "white", fontSize: "20px" }}>Deactivate Account</h4>
              <p className="mb-0 opacity-75" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px" }}>
                Temporarily suspend your account
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{ padding: "24px" }}>
          <div className="text-center">
            <div className="mb-4">
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <Icon name="pause circle" size="large" style={{ color: "white", fontSize: "40px", margin: 0 }} />
              </div>
            </div>

            <h5 className="fw-bold text-dark mb-3">
              Are you sure you want to deactivate your account?
            </h5>

            <p className="text-muted mb-4">
              This will temporarily suspend your account and stop billing. You can reactivate your account later, but you'll need to contact support to restore access.
            </p>

            <div className="d-flex flex-column gap-2 mb-4" style={{ textAlign: "left", maxWidth: "400px", margin: "0 auto" }}>
              <div className="d-flex align-items-start">
                <Icon name="pause" style={{ color: "#fa709a", marginRight: "8px", marginTop: "4px" }} />
                <div>
                  <strong>Account Suspension:</strong> Your account will be temporarily suspended
                </div>
              </div>
              <div className="d-flex align-items-start">
                <Icon name="credit card" style={{ color: "#fa709a", marginRight: "8px", marginTop: "4px" }} />
                <div>
                  <strong>Billing Stopped:</strong> All charges will stop immediately
                </div>
              </div>
              <div className="d-flex align-items-start">
                <Icon name="lock" style={{ color: "#fa709a", marginRight: "8px", marginTop: "4px" }} />
                <div>
                  <strong>Access Restricted:</strong> Users won't be able to access the system
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <Button
                onClick={() => setShowDeactivateModal(false)}
                disabled={isDeactivating}
                style={{ borderRadius: "10px" }}
              >
                <Icon name="cancel" />
                Cancel
              </Button>
              <Button
                onClick={handleDeactivateAccount}
                disabled={isDeactivating}
                loading={isDeactivating}
                style={{
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                  border: "none",
                  color: "white",
                  opacity: isDeactivating ? 0.7 : 1,
                }}
              >
                <Icon name="pause circle" />
                {isDeactivating ? "Deactivating..." : "Deactivate Account"}
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>

      {/* Delete Account Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="small"
        className="delete-account-modal"
        style={{
          height: "fit-content",
          margin: "auto",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
        }}
      >
        <Modal.Header
          className="delete-modal-header"
          style={{
            borderBottom: "1px solid #e9ecef",
            padding: "24px 24px 16px 24px",
            background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
            backgroundColor: "transparent",
            color: "white",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="trash alternate" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 className="mb-1 fw-bold" style={{ color: "white", fontSize: "20px" }}>Delete Company Account</h4>
              <p className="mb-0 opacity-75" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px" }}>
                Permanently delete your company account
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content>
          <div className="delete-content">
            <div className="delete-warning">
              <Icon name="exclamation triangle" size="large" style={{ color: '#ff6b6b' }} />
              <h4>This action cannot be undone!</h4>
              <p>
                You are about to permanently delete your company account and all associated data.
                This will immediately:
              </p>
              <ul>
                <li>Cancel your subscription and stop billing</li>
                <li>Log out all users from the system</li>
                <li>Delete all employee records and data</li>
                <li>Disable all integrations and API access</li>
                <li>Remove all company settings and configurations</li>
                <li>{t("app.dangerZone.deleteModalBulletPayroll")}</li>
                <li>{t("app.dangerZone.deleteModalBulletAttendance")}</li>
                <li>{t("app.dangerZone.deleteModalBulletDocuments")}</li>
                <li>{t("app.dangerZone.deleteModalBulletTimesheets")}</li>
              </ul>
            </div>

            <div className="delete-confirmation">
              <label>Type "DELETE" to confirm:</label>
              <Input
                placeholder="DELETE"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                error={deleteConfirmation !== "" && deleteConfirmation !== "DELETE"}
              />
              {deleteConfirmation !== "" && deleteConfirmation !== "DELETE" && (
                <p style={{ color: '#ff6b6b', fontSize: '0.875rem', marginTop: '8px' }}>
                  Please type "DELETE" exactly to confirm
                </p>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          borderRadius: "0 0 20px 20px",
          padding: "24px",
          borderTop: "1px solid #e9ecef"
        }}>
          <div className={`delete-modal-actions ${deleteConfirmation === "DELETE" ? "confirmed" : ""}`}>
            <Button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation("");
              }}
              disabled={isDeleting}
              className="delete-cancel-btn"
              style={{
                borderRadius: "10px",
                padding: "10px 24px",
                background: "#ffffff",
                border: "1px solid #e9ecef",
                color: "#495057",
                fontWeight: "500",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.target.style.background = "#f8f9fa";
                  e.target.style.borderColor = "#dee2e6";
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting) {
                  e.target.style.background = "#ffffff";
                  e.target.style.borderColor = "#e9ecef";
                }
              }}
            >
              <Icon name="cancel" />
              Cancel
            </Button>
            {deleteConfirmation === "DELETE" && (
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                loading={isDeleting}
                className="delete-confirm-btn"
                style={{
                  borderRadius: "10px",
                  padding: "10px 24px",
                  background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: "500",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  opacity: isDeleting ? 0.6 : 1,
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  animation: "slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.target.style.opacity = "0.9";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.target.style.opacity = "1";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                <Icon name="trash alternate" />
                {isDeleting ? "Deleting..." : "Delete Account"}
              </Button>
            )}
          </div>
        </Modal.Actions>
      </Modal>

      {/* Delete Success Modal */}
      <Modal
        open={showDeleteSuccessModal}
        onClose={() => {
          setShowDeleteSuccessModal(false);
          localStorage.clear();
          window.location.href = '/login';
        }}
        size="tiny"
        dimmer="blurring"
        centered
        className="delete-success-modal"
        style={{
          height: "fit-content",
          margin: "auto",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
        }}
      >
        <Modal.Header
          className="delete-success-modal-header"
          style={{
            borderBottom: "1px solid #e9ecef",
            padding: "24px 24px 16px 24px",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            backgroundColor: "transparent",
            color: "white",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="check circle" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 className="mb-1 fw-bold" style={{ color: "white", fontSize: "20px" }}>Account Deleted</h4>
              <p className="mb-0 opacity-75" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px" }}>
                Company account has been successfully deleted
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{ padding: "24px" }}>
          <div className="text-center">
            <div className="mb-4">
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <Icon name="check circle" size="large" style={{ color: "white", fontSize: "40px", margin: 0 }} />
              </div>
            </div>

            <h5 className="fw-bold text-dark mb-3">
              Company Account Deleted Successfully
            </h5>

            <p className="text-muted mb-4">
              Your company account and all associated data have been permanently deleted.
              You will be redirected to the login page shortly.
            </p>

            <div className="d-flex justify-content-center">
              <Button
                onClick={() => {
                  setShowDeleteSuccessModal(false);
                  localStorage.clear();
                  window.location.href = '/login';
                }}
                style={{
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  border: "none",
                  color: "white",
                }}
              >
                <Icon name="check circle" />
                Continue to Login
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>

      {/* Deactivate Success Modal */}
      <Modal
        open={showDeactivateSuccessModal}
        onClose={() => {
          setShowDeactivateSuccessModal(false);
          localStorage.clear();
          window.location.href = '/login';
        }}
        size="tiny"
        dimmer="blurring"
        centered
        className="deactivate-success-modal"
        style={{
          height: "fit-content",
          margin: "auto",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
        }}
      >
        <Modal.Header
          className="deactivate-success-modal-header"
          style={{
            borderBottom: "1px solid #e9ecef",
            padding: "24px 24px 16px 24px",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            backgroundColor: "transparent",
            color: "white",
            borderRadius: "20px 20px 0 0",
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="me-3"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="check circle" size="large" style={{ color: "white", margin: 0 }} />
            </div>
            <div>
              <h4 className="mb-1 fw-bold" style={{ color: "white", fontSize: "20px" }}>Account Deactivated</h4>
              <p className="mb-0 opacity-75" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "13px" }}>
                Your account has been successfully deactivated
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{ padding: "24px" }}>
          <div className="text-center">
            <div className="mb-4">
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                }}
              >
                <Icon name="check circle" size="large" style={{ color: "white", fontSize: "40px", margin: 0 }} />
              </div>
            </div>

            <h5 className="fw-bold text-dark mb-3">
              Account Deactivated Successfully
            </h5>

            <p className="text-muted mb-4">
              Your account has been temporarily suspended and billing has been stopped.
              You can reactivate your account by contacting support. You will be redirected to the login page shortly.
            </p>

            <div className="d-flex justify-content-center">
              <Button
                onClick={() => {
                  setShowDeactivateSuccessModal(false);
                  localStorage.clear();
                  window.location.href = '/login';
                }}
                style={{
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  border: "none",
                  color: "white",
                }}
              >
                <Icon name="check circle" />
                Continue
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default DangerZone;
